/**
 * O manual, montado a partir do menu.
 *
 * **Por que não é um documento.** Um manual escrito à parte envelhece no dia
 * em que uma tela muda, e ninguém descobre — porque nada quebra. Aqui ele é
 * montado dos mesmos destinos que a navegação usa, na mesma ordem, e o texto
 * de cada um é um campo obrigatório da definição dele: para os recursos de
 * cadastro, no catálogo da API; para as telas de tarefa, em
 * `ajudaDasTarefas.ts`. Tela nova sem manual **não compila**.
 *
 * **E ele mostra só o que esta escola tem.** Quem esconde Trilhas e Jornadas
 * em Ajustar o menu não lê sobre elas aqui. Um manual que descreve recursos
 * que a pessoa não vê é pior que nenhum: ensina a procurar o que não existe.
 *
 * **O roteiro é a única parte que não vem dos destinos**, e tem de ser assim:
 * ele é a ordem entre telas, e nenhuma tela isolada sabe dizer que vem antes
 * da outra.
 */

import { useEffect, useMemo, useState } from 'react'

import type { Grupo } from '../components/AppShell'
import { Icon } from '../components/Icon'

/**
 * Por onde começar, numa escola vazia.
 *
 * A ordem não é arbitrária: cada passo depende do anterior existir. Turma
 * precisa de curso e de unidade; grade curricular precisa de turma e de
 * disciplina; grade precisa de esquema com horários.
 */
const ROTEIRO: readonly { o: string; porque: string }[] = [
  { o: 'Unidades e Locais', porque: 'onde há aula. Num campus só, é uma linha.' },
  { o: 'Turnos', porque: 'manhã, tarde e noite, com a hora de início e de fim.' },
  {
    o: 'Esquemas',
    porque: 'os tempos de um dia. Gere os horários aqui — sem eles nenhuma grade fecha.',
  },
  { o: 'Cursos e Turmas', porque: 'e, em cada turma, qual esquema ela segue.' },
  { o: 'Disciplinas', porque: 'uma vez cada, com o código do catálogo.' },
  {
    o: 'Regimes e Professores',
    porque: 'o regime primeiro: é dele que o teto de carga de cada professor é herdado.',
  },
  {
    o: 'Disponibilidade',
    porque: 'por Importar, se forem muitos. Trezentos professores não se digitam.',
  },
  {
    o: 'Grade curricular',
    porque: 'turma, disciplina e quantas aulas por semana. Também por Importar.',
  },
  { o: 'Grade', porque: 'conferir o que falta, e mandar montar.' },
]

export function Ajuda({
  grupos,
  foco,
  onIr,
  aoMostrar,
}: {
  /** Os mesmos destinos do menu, na mesma ordem. */
  grupos: Grupo[]
  /** A tela de onde a pessoa veio. Aparece primeiro e marcada. */
  foco?: string
  onIr: (id: string) => void
  /** Vai para a tela e começa o tour dela. */
  aoMostrar: (id: string) => void
}) {
  const [busca, setBusca] = useState('')

  const termo = busca.trim().toLowerCase()
  const casa = (texto: string) => texto.toLowerCase().includes(termo)

  const filtrados = useMemo(() => {
    if (!termo) return grupos
    return grupos
      .map((g) => ({
        ...g,
        itens: g.itens.filter(
          (d) =>
            casa(d.rotulo) ||
            casa(d.ajuda.paraQue) ||
            d.ajuda.comoUsar.some(casa) ||
            (d.ajuda.cuidados ?? []).some(casa),
        ),
      }))
      .filter((g) => g.itens.length > 0)
    // `casa` fecha sobre `termo`, que é derivado de `busca`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupos, termo])

  const total = grupos.reduce((n, g) => n + g.itens.length, 0)
  const achados = filtrados.reduce((n, g) => n + g.itens.length, 0)

  const destaque = foco && foco !== 'ajuda' ? foco : undefined
  const temFoco = grupos.some((g) => g.itens.some((d) => d.id === destaque))

  /**
   * Leva a pessoa ao verbete da tela de onde ela veio.
   *
   * **Rolar em vez de repetir.** A primeira versão mostrava esse verbete de
   * novo no topo, e o mesmo texto aparecia duas vezes na mesma página — o
   * manual deixava de ser uma lista para ser uma lista com um prefácio que a
   * contradiz. Âncora resolve: o manual segue completo e na ordem do menu, e
   * quem chegou de uma tela começa a ler na dela.
   */
  useEffect(() => {
    if (!destaque || !temFoco || termo) return
    const alvo = document.getElementById(`ajuda-${destaque}`)
    alvo?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [destaque, temFoco, termo])

  return (
    <>
      <header className="page-head">
        <h1>Manual</h1>
        <p className="page-head__note">
          Cada tela do menu desta escola, na mesma ordem, e como operar. O que está escondido em
          Ajustar o menu também não aparece aqui.
        </p>
      </header>

      <div className="field ajuda__busca no-print">
        <label className="field__label" htmlFor="ajuda-busca">
          Procurar
        </label>
        <input
          id="ajuda-busca"
          type="search"
          value={busca}
          placeholder="janela, matrícula, geminada…"
          onChange={(e) => setBusca(e.target.value)}
        />
        <span className="field__hint">
          {termo ? `${achados} de ${total} telas` : `${total} telas`}
        </span>
      </div>

      {!termo && (
        <section className="section">
          <div className="section__head">
            <h2>Por onde começar</h2>
            <span className="field__hint">numa escola vazia</span>
          </div>
          <p className="callout">
            <strong>Cada passo depende do anterior existir.</strong>
            <span>
              {' '}
              Turma precisa de curso e de unidade; a grade curricular precisa de turma e de
              disciplina; a grade precisa de esquema com horários. Fora dessa ordem, as telas pedem
              coisas que ainda não foram criadas.
            </span>
          </p>
          <ol className="ajuda__roteiro">
            {ROTEIRO.map((passo) => (
              <li key={passo.o}>
                <strong>{passo.o}</strong> — {passo.porque}
              </li>
            ))}
          </ol>

          {/* Vale para as vinte telas de cadastro, porque elas são uma só.
              Fica aqui, e não repetido no verbete de cada uma, pelo mesmo
              motivo que a tela é uma: vinte cópias divergem. */}
          <h3 className="sub-head">O que vale em toda tela de cadastro</h3>
          <ul className="ajuda__cuidados">
            <li>
              <strong>Procurar.</strong> A partir de oito registros aparece uma caixa de busca.
              Ela olha todos os campos, sem acento e sem diferenciar maiúscula — e procura pelo
              nome do que está referenciado, não pelo código. Digitar “AD1A” acha a oferta da
              turma AD1A.
            </li>
            <li>
              <strong>Editar abre por cima.</strong> O formulário vem num painel à direita, com a
              lista continuando atrás. <kbd>Esc</kbd> fecha sem salvar, e o foco volta para a
              linha de onde você saiu — o seu lugar na lista não se perde.
            </li>
            <li>
              <strong>Relações e horários só aparecem ao editar.</strong> Ambos apontam para o
              registro, e ele precisa existir antes. É a ordem, não uma limitação.
            </li>
            <li>
              <strong>Dois nomes iguais nunca aparecem iguais.</strong> Quando dois professores
              têm o mesmo nome, a lista de escolha mostra a matrícula ao lado de cada um — “SORAYA
              LIMA · mat. 1001”. Se aparecer “sem matrícula”, é o cadastro pedindo a matrícula
              exatamente onde ela faz falta. E os imports de demanda e de pessoal não escolhem
              entre homônimos: a aula entra sem professor e o aviso diz quem são os candidatos.
            </li>
          </ul>
        </section>
      )}

      {filtrados.map((grupo) => (
        <section className="section" key={grupo.rotulo}>
          <div className="section__head">
            <h2>{grupo.rotulo}</h2>
            <span className="field__hint">
              {grupo.itens.length} {grupo.itens.length === 1 ? 'tela' : 'telas'}
            </span>
          </div>

          <div className="stack">
            {grupo.itens.map((destino) => (
              <article
                className={
                  destino.id === destaque ? 'ajuda__verbete ajuda__verbete--foco' : 'ajuda__verbete'
                }
                id={`ajuda-${destino.id}`}
                key={destino.id}
              >
                <h3 className="ajuda__titulo">
                  <span className="ajuda__icone">
                    <Icon name={destino.icone} />
                  </span>
                  {destino.rotulo}
                  {destino.id === destaque && (
                    <span className="field__hint no-print">a tela que você tem aberta</span>
                  )}
                  {/* Mostrar na tela só aparece onde há tour. Apontar para
                      uma tela de três campos seria mais lento que ler. */}
                  {destino.ajuda.tour && destino.ajuda.tour.length > 0 && (
                    <button
                      type="button"
                      className="btn btn--secondary ajuda__tour no-print"
                      onClick={() => aoMostrar(destino.id)}
                    >
                      Mostrar na tela
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn--ghost ajuda__abrir no-print"
                    onClick={() => onIr(destino.id)}
                  >
                    Abrir
                  </button>
                </h3>
                <Verbete destino={destino} />
              </article>
            ))}
          </div>
        </section>
      ))}

      {termo && achados === 0 && (
        <p className="empty__body">
          Nada no manual fala de “{busca.trim()}”. Se é uma dúvida sobre o que o sistema deveria
          fazer e não faz, ela vale mais escrita do que procurada.
        </p>
      )}
    </>
  )
}

/** O corpo de um verbete: para que serve, os passos, e o que dá errado. */
function Verbete({ destino }: { destino: Grupo['itens'][number] }) {
  const { paraQue, comoUsar, cuidados } = destino.ajuda
  return (
    <>
      <p className="ajuda__paraque">{paraQue}</p>
      <ol className="ajuda__passos">
        {comoUsar.map((passo) => (
          <li key={passo}>{passo}</li>
        ))}
      </ol>
      {cuidados && cuidados.length > 0 && (
        <ul className="ajuda__cuidados">
          {cuidados.map((cuidado) => (
            <li key={cuidado}>
              <Icon name="aviso" size={13} />
              <span>{cuidado}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
